import React, { useEffect, useState } from 'react';
import { ChartRendererSupabase } from './ChartRendererSupabase';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface ExtremeCommutingPieChartProps {
  location: string;
}

interface PieChartConfig {
  chartData: any;
  chartOptions: any;
  dataYear: number | null;
}

/** Map ACS commute_time category to display buckets: Under 15, 15-30, 30-45, 45-60, 60-90, Over 90 min */
function formatCommuteTimeLabel(category: string): string {
  if (!category || category === 'total') return 'Total';
  const normalized = category.toLowerCase().replace(/_/g, ' ');
  if (normalized.includes('90') || normalized.includes('or more')) return 'Over 90 min';
  if (normalized.includes('60')) return '60-90 min';
  if (normalized.includes('45')) return '45-60 min';
  if (normalized.includes('35')) return '30-45 min';
  if (normalized.includes('30')) return '30-45 min';
  if (normalized.includes('25') || normalized.includes('20') || normalized.includes('15')) return '15-30 min';
  if (normalized.includes('10') || normalized.includes('less') || normalized.includes('under') || normalized.includes('5')) return 'Under 15 min';
  return category.replace(/_/g, ' ');
}

const PIE_COLORS = [
  '#22c55e', // green - short
  '#84cc16',
  '#eab308',
  '#f97316',
  '#ef4444', // red - long
  '#7c3aed', // purple - extreme
];

export const ExtremeCommutingPieChart: React.FC<ExtremeCommutingPieChartProps> = ({ location }) => {
  const [localChart, setLocalChart] = useState<PieChartConfig | null>(null);
  const [stateChart, setStateChart] = useState<PieChartConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRowsForGeofips = async (geofips: number) => {
      const { data: viewData, error: viewError } = await supabaseClient
        .from('extreme_commuting_by_place')
        .select('year, commute_time, estimate')
        .eq('place_geofips', geofips)
        .order('year', { ascending: false });

      if (!viewError && viewData?.length) {
        return viewData;
      }

      const { data: directData, error: directError } = await supabaseClient
        .from('extreme_commuting')
        .select('year, commute_time, estimate')
        .eq('geofips', geofips)
        .order('year', { ascending: false });

      if (directError || !directData?.length) {
        throw new Error(viewError?.message || directError?.message || 'No extreme commuting data available.');
      }

      return directData;
    };

    const buildPieConfigFromRows = (
      rows: { year: number; commute_time: string; estimate: number }[],
      loc: string
    ): PieChartConfig => {
      const latestYear = rows[0]?.year ?? null;
      const yearRows = rows.filter((r) => r.year === latestYear && r.commute_time && r.commute_time !== 'total');
      // Aggregate by formatted label so duplicate labels become one slice
      const labelToEstimate = new Map<string, number>();
      for (const r of yearRows) {
        const label = formatCommuteTimeLabel(r.commute_time);
        const est = Number(r.estimate) || 0;
        labelToEstimate.set(label, (labelToEstimate.get(label) ?? 0) + est);
      }
      const order = ['Under 15 min', '15-30 min', '30-45 min', '45-60 min', '60-90 min', 'Over 90 min'];
      const labels = order.filter((l) => labelToEstimate.has(l));
      const values = labels.map((l) => labelToEstimate.get(l) ?? 0);
      const totalEstimate = values.reduce((sum, v) => sum + v, 0);

      if (totalEstimate === 0) {
        throw new Error(`No commute time breakdown available for ${loc} in the latest year.`);
      }

      const colors = labels.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]);

      return {
        chartData: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderColor: colors.map((c) => c),
              borderWidth: 2,
            },
          ],
        },
        chartOptions: {
          type: 'pie' as const,
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom' as const,
              labels: {
                usePointStyle: true,
                padding: 16,
                generateLabels: (chart: any) => {
                  const data = chart.data;
                  if (data?.labels?.length && data?.datasets?.[0]?.data) {
                    return data.labels.map((label: string, i: number) => {
                      const value = data.datasets[0].data[i];
                      const pct = totalEstimate > 0 ? ((value / totalEstimate) * 100).toFixed(1) : '0';
                      return {
                        text: `${label}: ${value.toLocaleString()} (${pct}%)`,
                        fillStyle: data.datasets[0].backgroundColor[i],
                        strokeStyle: data.datasets[0].borderColor[i],
                        lineWidth: 2,
                        pointStyle: 'circle',
                        hidden: false,
                        index: i,
                      };
                    });
                  }
                  return [];
                },
              },
            },
            tooltip: {
              callbacks: {
                label: (context: any) => {
                  const value = context.parsed;
                  const pct = totalEstimate > 0 ? ((value / totalEstimate) * 100).toFixed(1) : '0';
                  return `${context.label}: ${value.toLocaleString()} workers (${pct}%)`;
                },
              },
            },
          },
        },
        dataYear: latestYear,
      };
    };

    const loadData = async () => {
      if (!location) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        setLocalChart(null);
        setStateChart(null);

        await supabaseLocationService.initialize();
        const [placeGeofips, californiaGeofips] = await Promise.all([
          supabaseLocationService.getGeofips(location),
          supabaseLocationService.getGeofips('California'),
        ]);

        if (!placeGeofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        const localRows = await fetchRowsForGeofips(placeGeofips);
        setLocalChart(buildPieConfigFromRows(localRows, location));

        const isCaliforniaSelected = location.trim().toLowerCase() === 'california';
        if (!isCaliforniaSelected && californiaGeofips) {
          const californiaRows = await fetchRowsForGeofips(californiaGeofips);
          setStateChart(buildPieConfigFromRows(californiaRows, 'California'));
        }
      } catch (err) {
        console.error('ExtremeCommutingPieChart:', err);
        setError(err instanceof Error ? err.message : 'Failed to load extreme commuting data');
        setLocalChart(null);
        setStateChart(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [location]);

  if (loading) {
    return <div className="placeholder-chart">Loading extreme commuting data…</div>;
  }

  if (error) {
    return (
      <div className="placeholder-chart" style={{ color: '#b91c1c' }}>
        {error}
      </div>
    );
  }

  if (!localChart) {
    return <div className="placeholder-chart">No data available</div>;
  }

  const showStateComparison = !!stateChart;

  return (
    <div>
      <h3 style={{ marginBottom: '12px' }}>Commute time to work</h3>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showStateComparison ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
          gap: '16px',
          alignItems: 'stretch',
        }}
      >
        <div>
          <h4 style={{ margin: '0 0 8px 0', textAlign: 'center' }}>
            {location}
            {localChart.dataYear ? ` (${localChart.dataYear})` : ''}
          </h4>
          <ChartRendererSupabase
            chartData={localChart.chartData}
            chartOptions={localChart.chartOptions}
            height="420px"
            location={location}
          />
        </div>
        {showStateComparison && stateChart && (
          <div>
            <h4 style={{ margin: '0 0 8px 0', textAlign: 'center' }}>
              California
              {stateChart.dataYear ? ` (${stateChart.dataYear})` : ''}
            </h4>
            <ChartRendererSupabase
              chartData={stateChart.chartData}
              chartOptions={stateChart.chartOptions}
              height="420px"
              location="California"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtremeCommutingPieChart;
